// Utilidades de imagen: extraer colores dominantes de un logo y quitar el fondo (efecto sticker).

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

function toHex(r, g, b) {
  const h = (n) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, "0");
  return `#${h(r)}${h(g)}${h(b)}`;
}
function adjust(r, g, b, f) {
  // f>0 aclara hacia blanco, f<0 oscurece hacia negro
  if (f >= 0) return [r + (255 - r) * f, g + (255 - g) * f, b + (255 - b) * f];
  return [r * (1 + f), g * (1 + f), b * (1 + f)];
}

// Devuelve {brand, light, ink} a partir de la imagen (blob URL o dataURL).
export async function extractLogoColors(src) {
  const img = await loadImage(src);
  const size = 64;
  const c = document.createElement("canvas");
  c.width = size; c.height = size;
  const ctx = c.getContext("2d");
  ctx.drawImage(img, 0, 0, size, size);
  const { data } = ctx.getImageData(0, 0, size, size);
  let r = 0, g = 0, b = 0, n = 0;
  for (let i = 0; i < data.length; i += 4) {
    const a = data[i + 3];
    const pr = data[i], pg = data[i + 1], pb = data[i + 2];
    if (a < 128) continue; // ignora transparente
    if (pr > 238 && pg > 238 && pb > 238) continue; // ignora blanco/fondo claro
    if (pr < 14 && pg < 14 && pb < 14) continue; // ignora negro puro (bordes)
    const max = Math.max(pr, pg, pb), min = Math.min(pr, pg, pb);
    const sat = max === 0 ? 0 : (max - min) / max;
    const w = 1 + sat * 2; // pondera colores saturados
    r += pr * w; g += pg * w; b += pb * w; n += w;
  }
  if (n === 0) { // fallback: promedio simple
    for (let i = 0; i < data.length; i += 4) { r += data[i]; g += data[i + 1]; b += data[i + 2]; n++; }
  }
  r /= n; g /= n; b /= n;
  const [lr, lg, lb] = adjust(r, g, b, 0.25);
  const [ir, ig, ib] = adjust(r, g, b, -0.4);
  return { brand: toHex(r, g, b), light: toHex(lr, lg, lb), ink: toHex(ir, ig, ib) };
}

// Quita el fondo sólido (relleno por difusión desde los bordes) para dejar el logo como sticker. Devuelve un Blob PNG.
export async function removeBackground(file) {
  const url = URL.createObjectURL(file);
  try {
    const img = await loadImage(url);
    const maxDim = 900;
    let w = img.naturalWidth, h = img.naturalHeight;
    if (Math.max(w, h) > maxDim) { const s = maxDim / Math.max(w, h); w = Math.round(w * s); h = Math.round(h * s); }
    const c = document.createElement("canvas");
    c.width = w; c.height = h;
    const ctx = c.getContext("2d");
    ctx.drawImage(img, 0, 0, w, h);
    const imgData = ctx.getImageData(0, 0, w, h);
    const d = imgData.data;
    const idx = (x, y) => (y * w + x) * 4;

    // Color de fondo = promedio de los píxeles del borde opacos
    let br = 0, bg = 0, bb = 0, cnt = 0;
    const addBorder = (x, y) => { const i = idx(x, y); if (d[i + 3] > 200) { br += d[i]; bg += d[i + 1]; bb += d[i + 2]; cnt++; } };
    for (let x = 0; x < w; x++) { addBorder(x, 0); addBorder(x, h - 1); }
    for (let y = 0; y < h; y++) { addBorder(0, y); addBorder(w - 1, y); }
    if (cnt === 0) { URL.revokeObjectURL(url); return file; } // ya transparente
    br /= cnt; bg /= cnt; bb /= cnt;

    const tol = 62, tolSq = tol * tol;
    const dist2 = (i) => { const dr = d[i] - br, dg = d[i + 1] - bg, db = d[i + 2] - bb; return dr * dr + dg * dg + db * db; };

    // Flood-fill BFS desde todos los píxeles del borde que se parecen al fondo
    const visited = new Uint8Array(w * h);
    const stack = [];
    const pushIf = (x, y) => {
      if (x < 0 || y < 0 || x >= w || y >= h) return;
      const p = y * w + x; if (visited[p]) return; visited[p] = 1;
      if (dist2(p * 4) <= tolSq) { d[p * 4 + 3] = 0; stack.push(x, y); }
    };
    for (let x = 0; x < w; x++) { pushIf(x, 0); pushIf(x, h - 1); }
    for (let y = 0; y < h; y++) { pushIf(0, y); pushIf(w - 1, y); }
    while (stack.length) {
      const y = stack.pop(), x = stack.pop();
      pushIf(x + 1, y); pushIf(x - 1, y); pushIf(x, y + 1); pushIf(x, y - 1);
    }

    // Suaviza el contorno: pixeles semi-parecidos junto a zona removida bajan alpha
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const p = y * w + x, i = p * 4;
        if (d[i + 3] === 0) continue;
        const dd = dist2(i);
        if (dd <= tolSq * 2.2) {
          const near = (x > 0 && d[(p - 1) * 4 + 3] === 0) || (x < w - 1 && d[(p + 1) * 4 + 3] === 0) ||
                       (y > 0 && d[(p - w) * 4 + 3] === 0) || (y < h - 1 && d[(p + w) * 4 + 3] === 0);
          if (near) d[i + 3] = Math.round(d[i + 3] * 0.4);
        }
      }
    }

    ctx.putImageData(imgData, 0, 0);
    const blob = await new Promise((res) => c.toBlob(res, "image/png"));
    URL.revokeObjectURL(url);
    return blob || file;
  } catch {
    URL.revokeObjectURL(url);
    return file;
  }
}
